import { replace } from 'out-of-character';
import { tooltipFactory } from '@milkdown/plugin-tooltip';
import { usePluginViewContext } from '@prosemirror-adapter/react';
import { useRef, useState, useCallback, useEffect } from 'react';
import { editorViewCtx } from '@milkdown/kit/core';
import { Button, Input } from '@brainfish-ai/components';
import { X } from '@phosphor-icons/react';
import type { Ctx } from '@milkdown/kit/ctx';
import type { ReactNode } from 'react';

import './providers'; // Auto-register all providers
import { detectEmbedType } from './registry';
import { embedNode } from './embed';
import { useEditorAction } from '../../hooks/useEditorAction';

export const EMBED_TOOLTIP_NAME = 'embed-tooltip';

/**
 * Configuration for an embed type
 */
export interface EmbedConfig {
  /** Display name shown in tooltip header */
  label: string;
  /** Icon component to show in header */
  icon: ReactNode;
  /** Placeholder text for the input */
  placeholder: string;
  /** Validates if the URL is valid for this embed type */
  isValidUrl: (url: string) => boolean;
  /** Error message when URL is invalid */
  invalidUrlMessage: string;
}

// Tooltip state scoped per editor instance
type TooltipState = {
  show: boolean;
  position: { top: number; left: number };
  config: EmbedConfig | null;
};

const defaultState: TooltipState = {
  show: false,
  position: { top: 0, left: 0 },
  config: null,
};

// Registry to map Milkdown ctx to React setState (scopes state per editor instance)
const ctxToSetState = new WeakMap<Ctx, (state: TooltipState) => void>();

/**
 * Create the tooltip plugin using Milkdown's tooltipFactory
 */
export const embedTooltip = tooltipFactory(EMBED_TOOLTIP_NAME);

/**
 * Shows the embed tooltip at the current cursor position.
 * Uses ctx to lookup the correct editor instance's state setter.
 */
export function showEmbedTooltip(ctx: Ctx, config: EmbedConfig) {
  const setState = ctxToSetState.get(ctx);
  if (!setState) {
    console.warn('showEmbedTooltip: No EmbedTooltipView registered for this editor ctx');

    return;
  }

  const view = ctx.get(editorViewCtx);
  const { state } = view;
  const { from } = state.selection;

  const coords = view.coordsAtPos(from);
  const editorRect = view.dom.getBoundingClientRect();

  setState({
    show: true,
    position: {
      top: coords.bottom - editorRect.top + 8,
      left: coords.left - editorRect.left,
    },
    config,
  });
}

/**
 * React component for embed URL input tooltip
 * Renders based on the current config (YouTube, Vimeo, etc.)
 * Self-registers its state setter so showEmbedTooltip can find it.
 */
export const EmbedTooltipView = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { view } = usePluginViewContext();
  const { getInstance } = useEditorAction();
  const ctx = getInstance()?.ctx;

  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [tooltipState, setTooltipState] = useState<TooltipState>(defaultState);
  const { config } = tooltipState;
  const prevShowRef = useRef(tooltipState.show);

  // Register this component's setState so showEmbedTooltip can find it
  useEffect(() => {
    if (ctx) {
      ctxToSetState.set(ctx, setTooltipState);

      return () => {
        ctxToSetState.delete(ctx);
      };
    }
  }, [ctx]);

  // Reset state and focus input when tooltip becomes visible
  useEffect(() => {
    if (tooltipState.show && !prevShowRef.current) {
      setInputValue('');
      setError(null);
      const timeoutId = setTimeout(() => inputRef.current?.focus(), 50);
      prevShowRef.current = tooltipState.show;

      return () => clearTimeout(timeoutId);
    }
    prevShowRef.current = tooltipState.show;
  }, [tooltipState.show]);

  const handleClose = useCallback(() => {
    setTooltipState(defaultState);
    view.focus();
  }, [view]);

  const handleSubmit = useCallback(() => {
    if (!config) return;

    const url = replace(inputValue.trim());

    if (!url) {
      setError('Please enter a URL');

      return;
    }

    if (!config.isValidUrl(url)) {
      setError(config.invalidUrlMessage);

      return;
    }

    if (!ctx) {
      setError('Editor not ready');

      return;
    }

    try {
      // Create the embed node
      const embedType = detectEmbedType(url);
      const node = embedNode.type(ctx).create({ href: url, embedType });

      // Get editor view and state
      const editorView = ctx.get(editorViewCtx);
      const { state } = editorView;
      const { tr, selection } = state;
      const { $from } = selection;

      // Replace the entire current block (paragraph) with the embed node
      const blockStart = $from.before($from.depth);
      const blockEnd = $from.after($from.depth);

      const transaction = tr.replaceWith(blockStart, blockEnd, node);
      editorView.dispatch(transaction.scrollIntoView());

      // Close tooltip and focus editor
      setTooltipState(defaultState);
      setTimeout(() => view.focus(), 0);
    } catch (err) {
      console.error('Failed to insert embed:', err);
      setError('Failed to insert embed');
    }
  }, [inputValue, ctx, view, config]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        handleSubmit();
        break;
      case 'Escape':
        e.preventDefault();
        handleClose();
        break;
    }
  };

  if (!tooltipState.show || !config) return null;

  return (
    <div
      data-name={EMBED_TOOLTIP_NAME}
      className="milkdown-toolbar absolute z-50 flex flex-col bg-surface border border-border rounded-lg shadow-lg p-3 min-w-[320px]"
      style={{
        top: `${tooltipState.position.top}px`,
        left: `${tooltipState.position.left}px`,
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        {config.icon}
        <span className="font-medium text-sm">{config.label}</span>
        <Button variant="ghost" size="icon" className="ml-auto h-6 w-6" onClick={handleClose}>
          <X size={14} />
        </Button>
      </div>

      <div className="flex gap-2 items-center">
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          placeholder={config.placeholder}
          className="flex-1 h-8"
        />
        <Button onClick={handleSubmit} size="sm" className="text-dark">
          Embed
        </Button>
      </div>

      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
};
